import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";
import {
  LogOut, ShieldCheck, TrendingUp, Users, Receipt, Clock, BarChart3, ArrowRight,
  Calendar, CheckCircle2, XCircle,
} from "lucide-react";

const STATUS_META = {
  SUCCESS: { color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30", icon: CheckCircle2, label: "BERJAYA" },
  PENDING: { color: "text-neutral-400", bg: "bg-white/5 border-white/10", icon: Clock, label: "MENUNGGU" },
  FAILED:  { color: "text-red-400", bg: "bg-red-500/10 border-red-500/20", icon: XCircle, label: "GAGAL" },
};

function Stat({ label, value, hint, icon, accent, danger }) {
  return (
    <div className={`bg-[#101010] border rounded-xl p-5 ${danger ? "border-red-500/30" : accent ? "border-amber-500/30" : "border-white/10"}`}>
      <div className="text-xs text-neutral-500 uppercase tracking-wider font-mono flex items-center gap-2">
        {icon} {label}
      </div>
      <div className={`font-display text-3xl sm:text-4xl font-bold mt-2 ${danger ? "text-red-400" : accent ? "text-amber-400" : "text-white"}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}

function rm(v) { return `RM${Number(v || 0).toFixed(2)}`; }

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/dashboard");
        setData(data);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/admin/login");
  };

  const maxDaily = Math.max(...(data?.daily_revenue || []).map((d) => d.revenue_rm), 1);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Brand size="sm" showTagline={false} />
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-500/80 font-mono">
              <ShieldCheck size={12} /> Pentadbir
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Link to="/admin" className="px-3 py-2 rounded-md text-sm bg-amber-500/10 text-amber-400" data-testid="admin-nav-dashboard">Dashboard</Link>
            <Link to="/admin/whitelist" className="px-3 py-2 rounded-md text-sm text-neutral-400 hover:text-white hover:bg-white/5" data-testid="admin-nav-whitelist">Whitelist</Link>
            <button
              onClick={logout}
              className="ml-2 flex items-center gap-2 px-3 py-2 rounded-md text-sm text-neutral-400 hover:text-amber-400 hover:bg-white/5"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} /> Keluar
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-500/80 font-semibold mb-2">Overview</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold">Dashboard</h1>
        </div>

        {error && (
          <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-4 py-3 mb-4">{error}</div>
        )}

        {loading ? (
          <div className="text-neutral-500 text-center py-12">Memuatkan…</div>
        ) : data ? (
          <>
            {/* Revenue stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="revenue-stats">
              <Stat label="Hari Ini" value={rm(data.revenue.today_rm)} hint={`${data.transactions.today_paid} transaksi`} icon={<TrendingUp size={14} />} accent />
              <Stat label="Bulan Ini" value={rm(data.revenue.month_rm)} icon={<Calendar size={14} />} />
              <Stat label="30 Hari" value={rm(data.revenue.last_30d_rm)} icon={<BarChart3 size={14} />} />
              <Stat label="Sepanjang Masa" value={rm(data.revenue.total_rm)} hint={`${data.transactions.paid} transaksi berjaya`} icon={<Receipt size={14} />} />
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mb-8">
              <Stat label="Jumlah Ahli" value={data.subscribers.total} icon={<Users size={14} />} />
              <Stat label="Ahli Aktif" value={data.subscribers.active} hint={`${data.subscribers.lifetime} selamanya`} accent />
              <Stat label="Tamat Tempoh" value={data.subscribers.expired} danger={data.subscribers.expired > 0} />
            </div>

            {/* Daily chart */}
            <div className="bg-[#101010] border border-white/10 rounded-xl p-5 mb-8" data-testid="daily-chart">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-display text-lg font-semibold">Pendapatan Harian (30 hari)</div>
                  <div className="text-xs text-neutral-500">{data.daily_revenue.length} hari ada transaksi</div>
                </div>
              </div>
              {data.daily_revenue.length === 0 ? (
                <div className="text-center text-neutral-500 text-sm py-8">Tiada transaksi dalam 30 hari yang lepas.</div>
              ) : (
                <div className="flex items-end gap-1 h-40">
                  {data.daily_revenue.map((d) => {
                    const h = Math.max(4, Math.round((d.revenue_rm / maxDaily) * 100));
                    return (
                      <div
                        key={d.date}
                        className="flex-1 relative group"
                        title={`${d.date}: ${rm(d.revenue_rm)}`}
                      >
                        <div
                          className="w-full bg-amber-500/70 hover:bg-amber-400 transition-colors rounded-t"
                          style={{ height: `${h}%` }}
                        />
                        <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black/90 border border-white/10 px-2 py-1 rounded text-[10px] text-amber-400 font-mono whitespace-nowrap z-10">
                          {rm(d.revenue_rm)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Tier breakdown */}
            <div className="bg-[#101010] border border-white/10 rounded-xl p-5 mb-8" data-testid="tier-breakdown">
              <div className="font-display text-lg font-semibold mb-4">Pecahan Pakej</div>
              {data.tier_breakdown.length === 0 ? (
                <div className="text-center text-neutral-500 text-sm py-4">Belum ada jualan.</div>
              ) : (
                <div className="space-y-2">
                  {data.tier_breakdown.map((t) => (
                    <div key={t.tier} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0">
                      <div>
                        <div className="text-sm text-white font-semibold">{t.label}</div>
                        <div className="text-[10px] text-neutral-500 font-mono">{t.tier}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-amber-400 font-bold font-mono">{rm(t.revenue_rm)}</div>
                        <div className="text-[10px] text-neutral-500">{t.count} transaksi</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent transactions */}
            <div className="bg-[#101010] border border-white/10 rounded-xl overflow-hidden" data-testid="recent-tx">
              <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="font-display text-lg font-semibold">Transaksi Terkini</div>
                <Link to="/admin/transactions" className="text-xs text-amber-400 hover:underline flex items-center gap-1">
                  Lihat Semua <ArrowRight size={12} />
                </Link>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#141414] text-left">
                      <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Emel</th>
                      <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Pakej</th>
                      <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">RM</th>
                      <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Status</th>
                      <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Tarikh</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_transactions.length === 0 ? (
                      <tr><td colSpan={5} className="px-5 py-8 text-center text-neutral-500">Tiada transaksi.</td></tr>
                    ) : data.recent_transactions.map((tx) => {
                      const meta = STATUS_META[tx.status] || STATUS_META.PENDING;
                      const Icon = meta.icon;
                      return (
                        <tr key={tx.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="px-5 py-3 text-white font-mono text-xs">{tx.user_email}</td>
                          <td className="px-5 py-3">{tx.tier_label}</td>
                          <td className="px-5 py-3 text-amber-400 font-bold font-mono">RM{tx.price_rm}</td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border uppercase tracking-wider font-mono ${meta.bg} ${meta.color}`}>
                              <Icon size={10} /> {meta.label}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-[11px] text-neutral-500 font-mono">
                            {tx.paid_at ? new Date(tx.paid_at).toLocaleString("ms-MY") : new Date(tx.created_at).toLocaleString("ms-MY")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
